/**
 * Achievement definitions and helpers.
 * Achievements are one-time events based on MageProfile.stats.
 */

import { levelFromXp } from "./mage-xp.js";
import type { MageStats, MageProfile } from "./mage-profile.js";

export interface Achievement {
  id: string;
  name: string;       // i18n key
  description: string; // i18n key
  condition: (stats: MageStats, profile: MageProfile) => boolean;
}

export const ACHIEVEMENTS: Achievement[] = [
  {
    id: "first_blood",
    name: "achievement.first_blood.name",
    description: "achievement.first_blood.desc",
    condition: (s) => s.totalKills >= 1,
  },
  {
    id: "first_victory",
    name: "achievement.first_victory.name",
    description: "achievement.first_victory.desc",
    condition: (s) => Object.values(s.stageVictories).some((v) => v > 0),
  },
  {
    id: "slayer_100",
    name: "achievement.slayer_100.name",
    description: "achievement.slayer_100.desc",
    condition: (s) => s.totalKills >= 100,
  },
  {
    id: "slayer_1000",
    name: "achievement.slayer_1000.name",
    description: "achievement.slayer_1000.desc",
    condition: (s) => s.totalKills >= 1000,
  },
  {
    id: "banisher_100",
    name: "achievement.banisher_100.name",
    description: "achievement.banisher_100.desc",
    condition: (s) => s.totalBanishes >= 100,
  },
  {
    id: "battles_10",
    name: "achievement.battles_10.name",
    description: "achievement.battles_10.desc",
    condition: (s) => s.totalBattles >= 10,
  },
  {
    id: "battles_50",
    name: "achievement.battles_50.name",
    description: "achievement.battles_50.desc",
    condition: (s) => s.totalBattles >= 50,
  },
  {
    id: "played_1h",
    name: "achievement.played_1h.name",
    description: "achievement.played_1h.desc",
    condition: (s) => s.totalTimePlayed >= 3600000,
  },
  {
    id: "fields_5",
    name: "achievement.fields_5.name",
    description: "achievement.fields_5.desc",
    condition: (s) => Object.keys(s.stageVictories).length >= 5,
  },
  {
    id: "fields_20",
    name: "achievement.fields_20.name",
    description: "achievement.fields_20.desc",
    condition: (s) => Object.keys(s.stageVictories).length >= 20,
  },
  {
    id: "priest_level_5",
    name: "achievement.priest_level_5.name",
    description: "achievement.priest_level_5.desc",
    condition: (_s, p) => levelFromXp(p.totalXp) >= 5,
  },
  {
    id: "priest_level_10",
    name: "achievement.priest_level_10.name",
    description: "achievement.priest_level_10.desc",
    condition: (_s, p) => levelFromXp(p.totalXp) >= 10,
  },
  // AS3 stage/career badges: mana from hurrying wave spawns (wavespeeding)
  {
    id: "early_wave_light_100",
    name: "achievement.early_wave_light_100.name",
    description: "achievement.early_wave_light_100.desc",
    condition: (s) => (s.manaFromEarlyWaveSpawns ?? 0) >= 100,
  },
  {
    id: "early_wave_light_2000",
    name: "achievement.early_wave_light_2000.name",
    description: "achievement.early_wave_light_2000.desc",
    condition: (s) => (s.manaFromEarlyWaveSpawns ?? 0) >= 2000,
  },
  {
    id: "early_wave_light_50000",
    name: "achievement.early_wave_light_50000.name",
    description: "achievement.early_wave_light_50000.desc",
    condition: (s) => (s.manaFromEarlyWaveSpawns ?? 0) >= 50000,
  },
  {
    id: "early_wave_light_100000",
    name: "achievement.early_wave_light_100000.name",
    description: "achievement.early_wave_light_100000.desc",
    condition: (s) => (s.manaFromEarlyWaveSpawns ?? 0) >= 100000,
  },
  {
    id: "early_wave_calls_500",
    name: "achievement.early_wave_calls_500.name",
    description: "achievement.early_wave_calls_500.desc",
    condition: (s) => (s.earlyWaveHurries ?? 0) >= 500,
  },
];

/** Returns IDs of all achievements currently satisfied by the profile. */
export function getUnlockedAchievements(profile: MageProfile): string[] {
  return ACHIEVEMENTS.filter((a) => a.condition(profile.stats, profile)).map((a) => a.id);
}

/** Checks which achievements are newly unlocked, mutates profile.unlockedAchievements, returns new IDs. */
export function checkNewAchievements(profile: MageProfile): string[] {
  const unlocked = getUnlockedAchievements(profile);
  const existing = new Set(profile.unlockedAchievements ?? []);
  const newOnes = unlocked.filter((id) => !existing.has(id));
  profile.unlockedAchievements = [...existing, ...newOnes];
  return newOnes;
}
